// src/main.js
// App entry: handles auth, loads data, renders the shell, wires actions.

import { getSession, signIn, signOut, onAuthChange, sendPasswordRecovery, supabase } from './lib/supabase.js';
import { state, store, refreshAll, persistSettings } from './lib/store.js';
import { navigate, render, getCurrentView } from './lib/router.js';
import { escapeHtml, toast, showModal, closeModal, fmtMoney, fmtDealMoney, getCompany, getContact, contactName, isAdmin } from './lib/helpers.js';
import {
  editCompany, saveCompany, deleteCompany,
  editContact, saveContact, deleteContact,
  editDeal, saveDeal, deleteDeal, setDealStage, setDealStatus, markDealWon, detachQuoteDoc,
  editTicket, saveTicket, deleteTicket, setTicketStage, closeTicket,
  editActivity, saveActivity, deleteActivity, deleteActivityConfirm, toggleActivity,
  clearQuickNote, saveQuickNote,
  uploadAttachmentClick, viewAttachment, downloadAttachment, deleteAttachmentConfirm
} from './lib/modals.js';
import { dataActions, adminActions } from './views.js';

// Module-level flag: once we enter recovery mode (either from the URL fragment
// on boot or from the PASSWORD_RECOVERY auth event), we stay locked in the
// recovery screen until the user either submits a new password or signs out.
// Prevents stray auth events from inadvertently bypassing the screen.
let _recoveryLocked = false;

// -------------------------------------------------------------
// AUTH SCREEN
// -------------------------------------------------------------
function renderAuthScreen(errorMsg) {
  const root = document.getElementById('app-root');
  root.innerHTML = `
    <div class="auth-screen">
      <div class="auth-card">
        <img class="auth-logo" src="/logo.png" alt="IGV Systems" />
        <h2>Sign in</h2>
        <div class="auth-note">IGV Systems CRM · team workspace</div>
        <div id="auth-error-box"></div>
        <div class="field">
          <label>Email</label>
          <input type="email" id="auth-email" autofocus autocomplete="email" />
        </div>
        <div class="field">
          <label>Password</label>
          <input type="password" id="auth-password" autocomplete="current-password" />
        </div>
        <button class="btn primary" id="auth-submit" style="width:100%; margin-top:6px;">Sign in</button>
        <div style="text-align:center; margin-top:14px;">
          <a id="auth-forgot" style="font-size: 13px; color: var(--accent); cursor: pointer; text-decoration: none;">Forgot your password?</a>
        </div>
        <div class="help" style="text-align:center; margin-top:14px;">
          Accounts are created by the admin in the Supabase dashboard.
        </div>
      </div>
    </div>
  `;
  if (errorMsg) {
    document.getElementById('auth-error-box').innerHTML = '<div class="auth-error">' + escapeHtml(errorMsg) + '</div>';
  }
  const submit = async () => {
    const email = document.getElementById('auth-email').value.trim();
    const password = document.getElementById('auth-password').value;
    if (!email || !password) { renderAuthScreen('Enter email and password'); return; }
    const btn = document.getElementById('auth-submit');
    btn.disabled = true; btn.textContent = 'Signing in…';
    try {
      const { data, error } = await signIn(email, password);
      if (error) throw error;
      state.session = data.session;
      renderLoadingScreen();
      await refreshAll();
      if (state.currentProfile && !state.currentProfile.isActive) {
        await signOut();
        state.session = null;
        renderAuthScreen('Your account has been deactivated. Contact an admin.');
        return;
      }
      if (state.currentProfile && state.currentProfile.requiresPasswordSetup) {
        renderPasswordSetupScreen();
        return;
      }
      renderAppShell();
    } catch (e) {
      console.error(e);
      renderAuthScreen(e.message || 'Sign-in failed. Check your email and password.');
    }
  };
  document.getElementById('auth-submit').onclick = submit;
  document.getElementById('auth-email').addEventListener('keydown', (e) => { if (e.key === 'Enter') document.getElementById('auth-password').focus(); });
  document.getElementById('auth-password').addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
  document.getElementById('auth-forgot').onclick = () => {
    // Prefill with whatever email they may have already typed
    const typedEmail = document.getElementById('auth-email').value.trim();
    renderForgotPasswordScreen(typedEmail);
  };
}

// -------------------------------------------------------------
// FORGOT PASSWORD SCREEN — enter email, send recovery link
// -------------------------------------------------------------
function renderForgotPasswordScreen(prefillEmail, message) {
  const root = document.getElementById('app-root');
  root.innerHTML = `
    <div class="auth-screen">
      <div class="auth-card">
        <img class="auth-logo" src="/logo.png" alt="IGV Systems" />
        <h2>Reset your password</h2>
        <div class="auth-note">We'll email you a link to set a new password.</div>
        <div id="fp-msg-box"></div>
        <div class="field">
          <label>Email</label>
          <input type="email" id="fp-email" autofocus autocomplete="email" value="${escapeHtml(prefillEmail || '')}" />
        </div>
        <button class="btn primary" id="fp-submit" style="width:100%; margin-top:6px;">Send reset link</button>
        <div style="text-align:center; margin-top:14px;">
          <a id="fp-back" style="font-size: 13px; color: var(--ink-muted); cursor: pointer; text-decoration: none;">Back to sign in</a>
        </div>
      </div>
    </div>
  `;
  if (message) {
    document.getElementById('fp-msg-box').innerHTML = '<div class="auth-error" style="background: rgba(61,214,140,0.10); border-color: rgba(61,214,140,0.40); color: var(--ok);">' + escapeHtml(message) + '</div>';
  }
  const submit = async () => {
    const email = document.getElementById('fp-email').value.trim();
    if (!email || !email.includes('@')) {
      document.getElementById('fp-msg-box').innerHTML = '<div class="auth-error">Enter a valid email address</div>';
      return;
    }
    const btn = document.getElementById('fp-submit');
    btn.disabled = true; btn.textContent = 'Sending…';
    try {
      const { error } = await sendPasswordRecovery(email);
      if (error) throw error;
      // Always show the same success message regardless of whether the email
      // exists — small but real protection against using this endpoint to
      // probe which addresses are registered.
      renderForgotPasswordScreen(email, 'If an account exists for this email, a reset link is on its way. Check your inbox (and spam folder).');
    } catch (e) {
      console.error(e);
      btn.disabled = false; btn.textContent = 'Send reset link';
      document.getElementById('fp-msg-box').innerHTML = '<div class="auth-error">' + escapeHtml(e.message || 'Could not send reset email. Try again.') + '</div>';
    }
  };
  document.getElementById('fp-submit').onclick = submit;
  document.getElementById('fp-email').addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
  document.getElementById('fp-back').onclick = () => renderAuthScreen();
}

// -------------------------------------------------------------
// RECOVERY PASSWORD SCREEN — set a new password after clicking the email link
// Reached via the PASSWORD_RECOVERY auth event. The user has a recovery
// session that can call auth.updateUser but otherwise has no app access.
// -------------------------------------------------------------
function renderRecoveryPasswordScreen(errorMsg) {
  const root = document.getElementById('app-root');
  const email = state.session && state.session.user ? state.session.user.email : '';
  root.innerHTML = `
    <div class="auth-screen">
      <div class="auth-card">
        <img class="auth-logo" src="/logo.png" alt="IGV Systems" />
        <h2>Set a new password</h2>
        <div class="auth-note">${email ? 'For ' + escapeHtml(email) + '.<br/>' : ''}Choose a new password to finish resetting your account.</div>
        <div id="rp-error-box"></div>
        <div class="field">
          <label>New password</label>
          <input type="password" id="rp-new" autocomplete="new-password" autofocus />
          <div class="help">At least 8 characters.</div>
        </div>
        <div class="field">
          <label>Confirm password</label>
          <input type="password" id="rp-confirm" autocomplete="new-password" />
        </div>
        <button class="btn primary" id="rp-submit" style="width:100%; margin-top:6px;">Set password & continue</button>
      </div>
    </div>
  `;
  if (errorMsg) {
    document.getElementById('rp-error-box').innerHTML = '<div class="auth-error">' + escapeHtml(errorMsg) + '</div>';
  }
  const submit = async () => {
    const a = document.getElementById('rp-new').value;
    const b = document.getElementById('rp-confirm').value;
    if (!a || a.length < 8) { renderRecoveryPasswordScreen('Password must be at least 8 characters'); return; }
    if (a !== b) { renderRecoveryPasswordScreen('Passwords do not match'); return; }
    const btn = document.getElementById('rp-submit');
    btn.disabled = true; btn.textContent = 'Saving…';
    try {
      // Update the password through Supabase auth
      const { error: upErr } = await supabase.auth.updateUser({ password: a });
      if (upErr) throw upErr;
      // Refresh workspace data so we have the user's profile loaded
      await refreshAll();
      // If they were a freshly-invited user who never completed setup, mark them done.
      // This means the password-setup screen won't fire for them after this.
      if (state.currentProfile && state.currentProfile.requiresPasswordSetup) {
        try {
          await store.profiles.completePasswordSetup(a);
        } catch (e) {
          // Already happened above via auth.updateUser; we only need the flag flip.
          // Fall back: update the profile row directly if possible.
          console.warn('completePasswordSetup secondary call failed; clearing flag directly', e);
        }
      }
      // Final state check before showing the app
      if (state.currentProfile && !state.currentProfile.isActive) {
        await signOut();
        state.session = null;
        renderAuthScreen('Your account has been deactivated. Contact an admin.');
        return;
      }
      toast('Password updated — welcome back');
      _recoveryLocked = false;
      renderAppShell();
    } catch (e) {
      console.error(e);
      renderRecoveryPasswordScreen(e.message || 'Could not set password. Try again.');
    }
  };
  document.getElementById('rp-submit').onclick = submit;
  document.getElementById('rp-confirm').addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
}

// -------------------------------------------------------------
// LOADING SCREEN
// -------------------------------------------------------------
function renderLoadingScreen() {
  document.getElementById('app-root').innerHTML = `
    <div class="loading-screen">
      <div class="spinner"></div>
      <div class="boot-title">IGV <span class="accent">Systems</span></div>
      <div class="boot-sub">Loading workspace…</div>
    </div>
  `;
}

// -------------------------------------------------------------
// PASSWORD SETUP SCREEN (first-login flow for invited users)
// Renders when a user's profile has requires_password_setup=true.
// They must set a password before accessing the app — there's no skip.
// -------------------------------------------------------------
function renderPasswordSetupScreen(errorMsg) {
  const root = document.getElementById('app-root');
  const email = state.session && state.session.user ? state.session.user.email : '';
  root.innerHTML = `
    <div class="auth-screen">
      <div class="auth-card">
        <img class="auth-logo" src="/logo.png" alt="IGV Systems" />
        <h2>Set your password</h2>
        <div class="auth-note">Welcome to IGV CRM, ${escapeHtml(email)}.<br/>Choose a password to finish setting up your account.</div>
        <div id="pwd-error-box"></div>
        <div class="field">
          <label>New password</label>
          <input type="password" id="pwd-new" autocomplete="new-password" autofocus />
          <div class="help">At least 8 characters.</div>
        </div>
        <div class="field">
          <label>Confirm password</label>
          <input type="password" id="pwd-confirm" autocomplete="new-password" />
        </div>
        <button class="btn primary" id="pwd-submit" style="width:100%; margin-top:6px;">Set password & continue</button>
        <div class="help" style="text-align:center; margin-top:14px;">
          You won't be able to use the app until your password is set.
        </div>
      </div>
    </div>
  `;
  if (errorMsg) {
    document.getElementById('pwd-error-box').innerHTML = '<div class="auth-error">' + escapeHtml(errorMsg) + '</div>';
  }
  const submit = async () => {
    const a = document.getElementById('pwd-new').value;
    const b = document.getElementById('pwd-confirm').value;
    if (!a || a.length < 8) { renderPasswordSetupScreen('Password must be at least 8 characters'); return; }
    if (a !== b) { renderPasswordSetupScreen('Passwords do not match'); return; }
    const btn = document.getElementById('pwd-submit');
    btn.disabled = true; btn.textContent = 'Saving…';
    try {
      await store.profiles.completePasswordSetup(a);
      toast('Password set — welcome aboard');
      renderAppShell();
    } catch (e) {
      console.error(e);
      renderPasswordSetupScreen(e.message || 'Could not set password. Try again.');
    }
  };
  document.getElementById('pwd-submit').onclick = submit;
  document.getElementById('pwd-confirm').addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
}

// -------------------------------------------------------------
// APP SHELL (sidebar + topbar + main)
// -------------------------------------------------------------
function renderAppShell() {
  const userEmail = state.session && state.session.user ? state.session.user.email : '';
  document.getElementById('app-root').innerHTML = `
    <div class="app">
      <aside class="sidebar">
        <div class="brand">
          <img src="/logo.png" alt="IGV Systems" />
          <div class="sub">CRM · Cloud workspace</div>
        </div>
        <nav class="nav">
          <div class="label">Workspace</div>
          <button data-view="dashboard">Dashboard</button>
          <button data-view="tasks">My tasks <span class="count" id="ct-tasks">0</span></button>

          <div class="label">Sales</div>
          <button data-view="pipeline">Deal pipeline</button>
          <button data-view="deals">Deals <span class="count" id="ct-deals">0</span></button>

          <div class="label">Service</div>
          <button data-view="tickets-board">Ticket board</button>
          <button data-view="tickets">Tickets <span class="count" id="ct-tickets">0</span></button>

          <div class="label">Records</div>
          <button data-view="companies">Companies <span class="count" id="ct-companies">0</span></button>
          <button data-view="contacts">Contacts <span class="count" id="ct-contacts">0</span></button>
          <button data-view="activities">Activity log <span class="count" id="ct-activities">0</span></button>

          <div class="label">System</div>
          <button data-view="data">Data</button>
          ${isAdmin() ? `
          <button data-view="settings">Settings</button>
          <button data-view="users">Users</button>
          ` : ''}
        </nav>
        <div class="foot">
          <div class="user">${escapeHtml(userEmail || 'Signed in')}${isAdmin() ? ' <span class="chip accent" style="margin-left: 4px; font-size: 9px;">Admin</span>' : ''}</div>
          <div>v2.3c.1 · Supabase</div>
          <button class="signout-btn" id="signout-btn">Sign out</button>
        </div>
      </aside>
      <div>
        <div class="topbar">
          <div class="global-search">
            <input type="text" id="global-search" placeholder="Search companies, contacts, deals, tickets…" autocomplete="off" />
            <div class="search-results" id="search-results"></div>
          </div>
          <div class="spacer"></div>
          <div class="qa">
            <button class="btn small" onclick="window._crm.quickAction('task')">+ Task</button>
            <button class="btn small" onclick="window._crm.quickAction('ticket')">+ Ticket</button>
            <button class="btn primary small" onclick="window._crm.quickAction('deal')">+ Deal</button>
          </div>
        </div>
        <main class="main" id="main"></main>
      </div>
    </div>
  `;

  // nav bindings
  document.querySelectorAll('nav.nav button').forEach(b => {
    b.addEventListener('click', () => navigate(b.dataset.view));
  });

  // sign out
  document.getElementById('signout-btn').addEventListener('click', async () => {
    if (!confirm('Sign out?')) return;
    await signOut();
    state.session = null;
    renderAuthScreen();
  });

  // global search
  setupGlobalSearch();

  // initial render
  navigate('dashboard');
}

// -------------------------------------------------------------
// GLOBAL SEARCH
// -------------------------------------------------------------
function setupGlobalSearch() {
  const input = document.getElementById('global-search');
  const results = document.getElementById('search-results');
  const close = () => { results.classList.remove('open'); results.innerHTML = ''; };

  input.addEventListener('input', () => {
    const q = input.value.trim().toLowerCase();
    if (!q) { close(); return; }
    const companies = state.companies.filter(c => (c.name + ' ' + (c.industry||'') + ' ' + (c.segment||'')).toLowerCase().includes(q)).slice(0, 5);
    const contacts = state.contacts.filter(c => {
      const co = getCompany(c.companyId);
      return (contactName(c) + ' ' + (c.email||'') + ' ' + (c.title||'') + ' ' + (co?co.name:'')).toLowerCase().includes(q);
    }).slice(0, 5);
    const deals = state.deals.filter(d => d.title.toLowerCase().includes(q)).slice(0, 5);
    const tickets = state.tickets.filter(t => (t.subject + ' ' + (t.description||'')).toLowerCase().includes(q)).slice(0, 5);

    if (!companies.length && !contacts.length && !deals.length && !tickets.length) {
      results.innerHTML = '<div class="empty-r">No matches</div>';
      results.classList.add('open');
      return;
    }

    let html = '';
    if (companies.length) {
      html += '<div class="group-hd">Companies</div>';
      html += companies.map(c => `<a data-nav="company-detail" data-id="${c.id}"><div>${escapeHtml(c.name)}</div><div class="sub">${escapeHtml(c.industry||'')}${c.segment?' · '+escapeHtml(c.segment):''}</div></a>`).join('');
    }
    if (contacts.length) {
      html += '<div class="group-hd">Contacts</div>';
      html += contacts.map(c => {
        const co = getCompany(c.companyId);
        return `<a data-nav="contact-detail" data-id="${c.id}"><div>${escapeHtml(contactName(c))}</div><div class="sub">${escapeHtml(c.title||'')}${co?' · '+escapeHtml(co.name):''}</div></a>`;
      }).join('');
    }
    if (deals.length) {
      html += '<div class="group-hd">Deals</div>';
      html += deals.map(d => {
        const co = getCompany(d.companyId);
        return `<a data-nav="deal-detail" data-id="${d.id}"><div>${escapeHtml(d.title)}</div><div class="sub">${escapeHtml(fmtDealMoney(d))}${co?' · '+escapeHtml(co.name):''}</div></a>`;
      }).join('');
    }
    if (tickets.length) {
      html += '<div class="group-hd">Tickets</div>';
      html += tickets.map(t => {
        const co = getCompany(t.companyId);
        return `<a data-nav="ticket-detail" data-id="${t.id}"><div>${escapeHtml(t.subject)}</div><div class="sub">${co?escapeHtml(co.name):''}</div></a>`;
      }).join('');
    }

    results.innerHTML = html;
    results.classList.add('open');

    results.querySelectorAll('a[data-nav]').forEach(a => {
      a.addEventListener('click', () => {
        const view = a.dataset.nav;
        const id = a.dataset.id;
        input.value = '';
        close();
        navigate(view, { id });
      });
    });
  });

  input.addEventListener('blur', () => setTimeout(close, 160));
  input.addEventListener('focus', () => { if (input.value.trim()) input.dispatchEvent(new Event('input')); });
}

// -------------------------------------------------------------
// QUICK ACTIONS (top bar buttons)
// -------------------------------------------------------------
function quickAction(kind) {
  if (kind === 'task') editActivity(null, { type: 'task' });
  else if (kind === 'ticket') editTicket(null);
  else if (kind === 'deal') editDeal(null);
  else if (kind === 'activity') editActivity(null);
  else if (kind === 'company') editCompany(null);
  else if (kind === 'contact') editContact(null);
}

// Switch the workspace default currency (changes which deals show on
// the kanban and what the workspace currency stat shows). Persists to localStorage.
function setWorkspaceCurrency(cur) {
  if (!state.settings.currencies.includes(cur)) return;
  state.settings.currency = cur;
  persistSettings();
  toast('Workspace currency: ' + cur);
  render();
}

// Switch the dashboard display currency toggle. Session-only (per-browser),
// persisted to localStorage so each team member's preference survives reloads.
function setDisplayCurrency(opt) {
  if (!['native', 'ZAR', 'USD', 'EUR'].includes(opt)) return;
  state.settings.displayCurrency = opt;
  persistSettings();
  render();
}

// -------------------------------------------------------------
// WINDOW BINDINGS — inline onclick handlers across views call window._crm.xxx
// -------------------------------------------------------------
window._crm = {
  // navigation
  navigate, closeModal, quickAction,
  // companies
  editCompany, saveCompany, deleteCompany,
  // contacts
  editContact, saveContact, deleteContact,
  // deals
  editDeal, saveDeal, deleteDeal, setDealStage, setDealStatus, markDealWon, detachQuoteDoc,
  // tickets
  editTicket, saveTicket, deleteTicket, setTicketStage, closeTicket,
  // activities
  editActivity, saveActivity, deleteActivity, deleteActivityConfirm, toggleActivity,
  // quick note
  clearQuickNote, saveQuickNote,
  // attachments
  uploadAttachmentClick, viewAttachment, downloadAttachment, deleteAttachmentConfirm,
  // workspace settings
  setWorkspaceCurrency, setDisplayCurrency,
  // admin
  saveFxRates: adminActions.saveFxRates,
  inviteUserModal: adminActions.inviteUserModal,
  sendInvite: adminActions.sendInvite,
  toggleAdmin: adminActions.toggleAdmin,
  toggleActive: adminActions.toggleActive,
  // data module
  exportData: dataActions.exportData,
  exportCSV: dataActions.exportCSV,
  saveSettings: dataActions.saveSettings,
  importLegacy: dataActions.importLegacy
};

// -------------------------------------------------------------
// BOOT
// -------------------------------------------------------------

// Detect whether the current URL is the result of a password-reset click.
// Supabase puts auth parameters in the URL fragment (#) using either:
//   - the legacy implicit flow: #access_token=...&type=recovery
//   - the newer PKCE flow:      #type=recovery (with the token exchanged via code)
// We also check for ?code= which Supabase uses for the PKCE redirect.
function isRecoveryUrl() {
  const hash = window.location.hash || '';
  if (hash.includes('type=recovery')) return true;
  const params = new URLSearchParams(window.location.search);
  if (params.get('type') === 'recovery') return true;
  return false;
}

async function boot() {
  const arrivedViaRecovery = isRecoveryUrl();
  if (arrivedViaRecovery) {
    _recoveryLocked = true;
    // Render the recovery screen IMMEDIATELY so the user never sees the dashboard
    // or any loading state. Supabase will fire PASSWORD_RECOVERY shortly after,
    // but we don't wait for it — we just show the screen up front.
    renderRecoveryPasswordScreen();
    // Clean up the URL so the recovery fragment isn't sitting around if they
    // navigate after completing the reset. This doesn't reload the page.
    try { history.replaceState(null, '', window.location.pathname); } catch (_) {}
  }

  // React to auth state changes (expired session, sign-out in another tab, etc.)
  onAuthChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY' && session) {
      // User clicked a password-reset link from email. They have a special
      // recovery session — show the set-new-password screen instead of the app.
      _recoveryLocked = true;
      state.session = session;
      renderRecoveryPasswordScreen();
      return;
    }
    // If we're locked in recovery mode, ignore everything else until the user
    // either completes the reset (which unlocks via renderAppShell) or signs out.
    if (_recoveryLocked && event !== 'SIGNED_OUT') return;

    if (event === 'SIGNED_OUT') {
      _recoveryLocked = false;
      state.session = null;
      renderAuthScreen();
    } else if (event === 'TOKEN_REFRESHED' && session) {
      state.session = session;
    }
  });

  // If we already rendered the recovery screen from the URL check above, bail —
  // the auth listener will keep us in recovery mode until they submit.
  if (arrivedViaRecovery) return;

  const session = await getSession();
  if (!session) {
    renderAuthScreen();
    return;
  }
  state.session = session;
  renderLoadingScreen();
  try {
    await refreshAll();
    // Safety check: if the profile exists but is deactivated, sign the user out.
    // (RLS would reject every query anyway, but better to give a clear message.)
    if (state.currentProfile && !state.currentProfile.isActive) {
      await signOut();
      state.session = null;
      renderAuthScreen('Your account has been deactivated. Contact an admin.');
      return;
    }
    // First-login password setup gate. New invitees come in via magic link
    // with no password set; they must set one before accessing the app.
    if (state.currentProfile && state.currentProfile.requiresPasswordSetup) {
      renderPasswordSetupScreen();
      return;
    }
    // If a PASSWORD_RECOVERY event fired while we were loading the workspace,
    // _recoveryLocked is now true — don't override the recovery screen.
    if (_recoveryLocked) return;
    renderAppShell();
  } catch (e) {
    console.error('Failed to load workspace', e);
    renderAuthScreen('Failed to load workspace: ' + (e.message || 'unknown error'));
  }
}

boot();
